import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { DeterministicMealSuggestionAdapter } from '../application/adapters';
import {
  BoundedPublicMealDemoAdapter,
  ConsentGatedPublicRailAdapter,
  PUBLIC_MEAL_DEMO_LIMITS,
  PUBLIC_MEAL_STOCK_OPTIONS,
  emitPublicMealDemoMetric,
  type PublicDemoRailState,
  type PublicMealDemoResult,
  type PublicMealStockId,
} from '../application/public-meal-demo';
import type { MessageKey } from '../i18n/catalog';
import type { ContextualRecipeReturn } from '../config/recipe-links';
import { mealMissingIngredients, mealTemplateDescription, mealTemplateTitle } from '../i18n/domain-labels';
import { useI18n } from '../i18n/I18nContext';
import type { QuantityUnit } from '../domain/model';
import { Button, Icon, SectionHeader, unitLabel } from './components';

const DEFAULT_GRAMS: Record<PublicMealStockId, number> = {
  vegetables: 500,
  prepared: 350,
  meat: 300,
  fish: 300,
  fruit: 400,
  dairy: 200,
  sauce: 250,
};

const initialStock = (): Record<PublicMealStockId, number | null> => ({
  vegetables: DEFAULT_GRAMS.vegetables,
  prepared: DEFAULT_GRAMS.prepared,
  meat: null,
  fish: null,
  fruit: DEFAULT_GRAMS.fruit,
  dairy: null,
  sauce: null,
});

const stockLabelKey = (id: PublicMealStockId): MessageKey => `publicMeal.stock.${id}` as MessageKey;

export const PublicMealDemo = ({
  recipeReturn = null,
  appHref = '/',
}: {
  recipeReturn?: ContextualRecipeReturn | null;
  appHref?: string;
}) => {
  const { locale, t, formatNumber } = useI18n();
  const engine = useMemo(
    () => new BoundedPublicMealDemoAdapter(new DeterministicMealSuggestionAdapter()),
    [],
  );
  const advertising = useMemo(() => new ConsentGatedPublicRailAdapter(), []);
  const [stock, setStock] = useState(initialStock);
  const [result, setResult] = useState<PublicMealDemoResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [railState, setRailState] = useState<PublicDemoRailState>('consent_required');

  useEffect(() => {
    let active = true;
    const media = window.matchMedia('(min-width: 1920px)');
    const refresh = () => {
      void advertising.request({ desktop: media.matches, consent: 'unknown' }).then((state) => {
        if (active) setRailState(state);
      });
    };
    refresh();
    media.addEventListener('change', refresh);
    return () => {
      active = false;
      media.removeEventListener('change', refresh);
    };
  }, [advertising]);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    emitPublicMealDemoMetric('outil_démarré');
    setBusy(true);
    const entries = PUBLIC_MEAL_STOCK_OPTIONS.flatMap((option) => {
      const grams = stock[option.id];
      return grams == null ? [] : [{ id: option.id, grams }];
    });
    const next = await engine.suggest(entries);
    setResult(next);
    setBusy(false);
    if (next.state === 'ready') emitPublicMealDemoMetric('résultat_obtenu');
  };

  const errorMessage = result == null || result.state === 'ready'
    ? null
    : result.state === 'rate_limited'
      ? t('publicMeal.error.rateLimit', { seconds: result.retryAfterSeconds })
      : result.state === 'degraded'
        ? t(`publicMeal.error.${result.reason}` as MessageKey)
        : t(`publicMeal.error.${result.reason}` as MessageKey);

  return (
    <div className="public-meal-demo-layout">
      <aside
        className="public-meal-demo-rail public-meal-demo-rail--left"
        aria-hidden="true"
        data-ad-rail="left"
        data-ad-state={railState}
        data-consent-gated="true"
      />
      <section className="public-meal-demo" aria-label={t('publicMeal.title')}>
        <div className="public-meal-demo__hero">
          <SectionHeader
            eyebrow={t('publicMeal.eyebrow')}
            title={t('publicMeal.title')}
            description={t('publicMeal.description')}
          />
          <div className="public-meal-demo__privacy">
            <Icon name="info" />
            <p>{t('publicMeal.privacy')}</p>
          </div>
          {recipeReturn != null ? (
            <div className="notice notice--info recipe-return-notice" role="status">
              <Icon name="arrow" />
              <p>{t('recipe.contextualDemo', {
                ingredient: recipeReturn.ingredientId.replaceAll('-', ' '),
                recipe: recipeReturn.recipeId.replaceAll('-', ' '),
              })}</p>
            </div>
          ) : null}
        </div>

        <form className="public-meal-demo__form" aria-label={t('publicMeal.formLabel')} onSubmit={submit}>
          <fieldset>
            <legend>{t('publicMeal.stockLegend')}</legend>
            <p>{t('publicMeal.stockHelp')}</p>
            <div className="public-meal-stock-grid">
              {PUBLIC_MEAL_STOCK_OPTIONS.map((option) => {
                const selected = stock[option.id] != null;
                const label = t(stockLabelKey(option.id));
                return (
                  <div className={`public-meal-stock${selected ? ' public-meal-stock--selected' : ''}`} key={option.id}>
                    <label className="public-meal-stock__choice">
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={(change) => setStock((current) => ({
                          ...current,
                          [option.id]: change.target.checked ? DEFAULT_GRAMS[option.id] : null,
                        }))}
                      />
                      <span>{label}</span>
                    </label>
                    {selected ? (
                      <label className="public-meal-stock__quantity">
                        <span>{t('publicMeal.quantityLabel', { item: label })}</span>
                        <span>
                          <input
                            type="number"
                            inputMode="numeric"
                            min={PUBLIC_MEAL_DEMO_LIMITS.minGrams}
                            max={PUBLIC_MEAL_DEMO_LIMITS.maxGramsPerEntry}
                            step="50"
                            value={stock[option.id] ?? DEFAULT_GRAMS[option.id]}
                            onChange={(change) => setStock((current) => ({
                              ...current,
                              [option.id]: Number(change.target.value),
                            }))}
                          />
                          <small>g</small>
                        </span>
                      </label>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </fieldset>
          <Button type="submit" disabled={busy} icon={<Icon name="meal" />}>
            {busy ? t('publicMeal.calculating') : t('publicMeal.calculate')}
          </Button>
        </form>

        {errorMessage != null ? (
          <div className="notice notice--warning public-meal-demo__error" role="status">
            <Icon name="warning" />
            <p>{errorMessage}</p>
          </div>
        ) : null}

        {result?.state === 'ready' ? (
          <section className="public-meal-demo__results" aria-live="polite" aria-labelledby="public-meal-results-title">
            <div className="public-meal-demo__results-heading">
              <div>
                <span className="eyebrow">{t('publicMeal.resultsEyebrow')}</span>
                <h2 id="public-meal-results-title">{t('publicMeal.resultsTitle')}</h2>
                <p>{t('publicMeal.resultsHelp')}</p>
              </div>
              <span className="public-meal-demo__result-count">
                {t('publicMeal.resultCount', { count: result.suggestions.length })}
              </span>
            </div>
            {result.suggestions.length === 0 ? (
              <div className="notice notice--info"><Icon name="info" /><p>{t('publicMeal.noResult')}</p></div>
            ) : (
              <div className="public-meal-result-grid">
                {result.suggestions.map((suggestion, index) => {
                  const present = [...new Set(suggestion.containers.map((container) =>
                    t(stockLabelKey(container.categoryId as PublicMealStockId)),
                  ))];
                  const missing = mealMissingIngredients(
                    suggestion,
                    locale,
                    t('meals.quantityToCheck'),
                    (quantity, unit) => t('meals.missingQuantity', {
                      quantity: formatNumber(quantity, { maximumFractionDigits: 3 }),
                      unit: unitLabel(unit as QuantityUnit, locale, quantity),
                    }),
                  );
                  return (
                    <article className="public-meal-result-card" key={suggestion.template.id}>
                      <span className="public-meal-result-card__number">{String(index + 1).padStart(2, '0')}</span>
                      <div>
                        <span className="eyebrow">{t('publicMeal.canonicalResult')}</span>
                        <h3>{mealTemplateTitle(suggestion.template, locale)}</h3>
                        <p>{mealTemplateDescription(suggestion.template, locale)}</p>
                      </div>
                      <div>
                        <strong>{t('publicMeal.uses')}</strong>
                        <p>{present.join(t('common.separator'))}</p>
                      </div>
                      <div className="public-meal-result-card__missing">
                        <strong>{t('publicMeal.toComplete')}</strong>
                        <p>{missing.length > 0 ? missing.join(t('common.separator')) : t('meals.nothingMissing')}</p>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
            <dl className="public-meal-demo__provenance">
              <div><dt>{t('publicMeal.sourceLabel')}</dt><dd>{t('publicMeal.sourceValue', { version: result.metadata.engineVersion })}</dd></div>
              <div><dt>{t('publicMeal.coverageLabel')}</dt><dd>{t('publicMeal.coverageValue', { templates: result.metadata.templateCount, categories: result.metadata.selectedCategoryCount })}</dd></div>
              <div><dt>{t('publicMeal.freshnessLabel')}</dt><dd>{t('publicMeal.freshnessValue')}</dd></div>
              <div><dt>{t('publicMeal.stateLabel')}</dt><dd>{t('publicMeal.stateValue')}</dd></div>
            </dl>
          </section>
        ) : null}

        {result?.state === 'ready' || result?.state === 'degraded' ? (
          <div className="public-meal-demo__cta">
            <div>
              <strong>{t('publicMeal.ctaTitle')}</strong>
              <p>{t('publicMeal.ctaHelp')}</p>
            </div>
            <a className="button button--primary" href={appHref} onClick={() => emitPublicMealDemoMetric('cta_app')}>
              {t('publicMeal.cta')}
            </a>
          </div>
        ) : null}
      </section>
      <aside
        className="public-meal-demo-rail public-meal-demo-rail--right"
        aria-hidden="true"
        data-ad-rail="right"
        data-ad-state={railState}
        data-consent-gated="true"
      />
    </div>
  );
};
